import { findCategoryMapping, getCategorySourceKey, normalizeSizeGroupSizes } from "./sourceMappingIdentity";

const EMPTY_ARRAY = [];

// Shared by the Categories popup and its completion indicator.
export const getCategorySizeCountIssues = ({
    categoryFieldName,
    categoryMappings = EMPTY_ARRAY,
    labelBrandProducts = EMPTY_ARRAY,
    sizeGroupsFromPOS = EMPTY_ARRAY
}) => {
    const valueIsPresent = value => value !== undefined && value !== null && value !== "";
    const productsByCategory = new Map();
    (Array.isArray(labelBrandProducts) ? labelBrandProducts : EMPTY_ARRAY).forEach(product => {
        const categoryName = categoryFieldName === "Product type" ? product?.productType : product?.category;
        const key = getCategorySourceKey(categoryName, product?.gender);
        if (!productsByCategory.has(key)) productsByCategory.set(key, []);
        productsByCategory.get(key).push(normalizeSizeGroupSizes(product?.sizes));
    });

    const groups = Array.isArray(sizeGroupsFromPOS) ? sizeGroupsFromPOS : EMPTY_ARRAY;
    return categoryMappings.map(category => {
        if (!valueIsPresent(category?.relatedSizeGroupNameFromPOS)) return null;
        const groupByID = valueIsPresent(category.relatedSizeGroupIdFromPOS)
            ? groups.find(item => String(item?.sizeGroupID) === String(category.relatedSizeGroupIdFromPOS))
            : null;
        const group = groupByID || groups.find(item => item?.sizeGroupName === category.relatedSizeGroupNameFromPOS);
        if (!Array.isArray(group?.sizeNames)) return null;

        const categoryKey = getCategorySourceKey(category.labelBrandCategoryName, category.labelBrandGenderName);
        const products = (productsByCategory.get(categoryKey) || EMPTY_ARRAY).filter(
            sizes =>
                !category.labelBrandSizeGroupSizes?.length ||
                sizes.every(size => category.labelBrandSizeGroupSizes.includes(size))
        );
        const brandSizeCount = products.reduce((max, sizes) => Math.max(max, sizes.length), 0);
        const posSizeNames = [...new Set(group.sizeNames.filter(valueIsPresent))];
        const posSizeCount = posSizeNames.length;
        if (brandSizeCount <= posSizeCount) return null;

        const missingSizeNames = [...new Set(products.flat())].filter(size => !posSizeNames.includes(size));
        return { brandSizeCount, posSizeCount, missingSizeNames, posSizeNames };
    });
};

export const getMissingSizeRows = ({
    categoryFieldName,
    categorySourceRows = EMPTY_ARRAY,
    relatedSizeGroupMappings = EMPTY_ARRAY,
    labelBrandProducts = EMPTY_ARRAY,
    sizeGroupsFromPOS = EMPTY_ARRAY
}) => {
    if (!categoryFieldName || !Array.isArray(categorySourceRows)) return [];

    const sourceKeys = new Set(
        categorySourceRows.map(category => getCategorySourceKey(category?.name, category?.gender))
    );
    const sizeGroupsByName = new Map(
        (Array.isArray(sizeGroupsFromPOS) ? sizeGroupsFromPOS : EMPTY_ARRAY).map(group => [group?.sizeGroupName, group])
    );
    const rowsByKey = new Map();
    (Array.isArray(labelBrandProducts) ? labelBrandProducts : EMPTY_ARRAY).forEach(product => {
        const categoryName = categoryFieldName === "Product type" ? product?.productType : product?.category;
        if (!sourceKeys.has(getCategorySourceKey(categoryName, product?.gender))) return;
        const brandSizes = normalizeSizeGroupSizes(product?.sizes);
        const categoryMapping = findCategoryMapping(
            relatedSizeGroupMappings,
            categoryName,
            product?.gender,
            brandSizes
        );
        const groupName = categoryMapping?.exportName;
        const group = sizeGroupsByName.get(groupName);
        if (!group) return;
        const posSizes = Array.isArray(group?.sizeNames) ? group.sizeNames : EMPTY_ARRAY;
        brandSizes.forEach(brandSize => {
            if (posSizes.some(posSize => posSize === brandSize)) return;
            const rowKey = JSON.stringify([groupName, brandSize]);
            if (!rowsByKey.has(rowKey)) rowsByKey.set(rowKey, { sizeGroupName: groupName, labelBrandName: brandSize });
        });
    });
    return [...rowsByKey.values()];
};

export const areMissingSizesMapped = (rows = EMPTY_ARRAY, conversions = EMPTY_ARRAY) => {
    if (!rows.length) return true;
    return rows.every(row =>
        conversions.some(
            group =>
                group?.sizeGroupName === row.sizeGroupName &&
                group?.sizes?.some(size => size?.labelBrandName === row.labelBrandName && !!size?.exportName)
        )
    );
};

// Returns UI row keys for distinct sizes on one product that resolve to the same POS size.
// Exact matches also participate, even though they do not appear in the missing-size table.
export const getSizeMappingConflicts = ({
    categoryFieldName,
    categorySourceRows = EMPTY_ARRAY,
    relatedSizeGroupMappings = EMPTY_ARRAY,
    labelBrandProducts = EMPTY_ARRAY,
    sizeGroupsFromPOS = EMPTY_ARRAY,
    mappings = EMPTY_ARRAY
}) => {
    const conflicts = new Set();
    if (!categoryFieldName || !Array.isArray(categorySourceRows)) return conflicts;

    const sourceKeys = new Set(
        categorySourceRows.map(category => getCategorySourceKey(category?.name, category?.gender))
    );
    const groupsByName = new Map(
        (Array.isArray(sizeGroupsFromPOS) ? sizeGroupsFromPOS : EMPTY_ARRAY).map(group => [group?.sizeGroupName, group])
    );
    const targetsByRow = new Map(
        mappings.map(mapping => [
            JSON.stringify([mapping?.sizeGroupName, mapping?.labelBrandName]),
            mapping?.exportName
        ])
    );

    (Array.isArray(labelBrandProducts) ? labelBrandProducts : EMPTY_ARRAY).forEach(product => {
        const categoryName = categoryFieldName === "Product type" ? product?.productType : product?.category;
        if (!sourceKeys.has(getCategorySourceKey(categoryName, product?.gender))) return;
        const groupName = findCategoryMapping(
            relatedSizeGroupMappings,
            categoryName,
            product?.gender,
            product?.sizes
        )?.exportName;
        const group = groupsByName.get(groupName);
        if (!group) return;

        const posSizes = Array.isArray(group.sizeNames) ? group.sizeNames : EMPTY_ARRAY;
        const rowsByTarget = new Map();
        const brandSizes = new Set(Array.isArray(product?.sizes) ? product.sizes : EMPTY_ARRAY);
        brandSizes.forEach(brandSize => {
            if (brandSize === null || brandSize === undefined || brandSize === "") return;
            const rowKey = JSON.stringify([groupName, brandSize]);
            const target = posSizes.includes(brandSize) ? brandSize : targetsByRow.get(rowKey);
            if (!target) return;
            if (!rowsByTarget.has(target)) rowsByTarget.set(target, []);
            rowsByTarget.get(target).push(rowKey);
        });
        rowsByTarget.forEach(rowKeys => {
            if (rowKeys.length > 1) rowKeys.forEach(key => conflicts.add(key));
        });
    });
    return conflicts;
};
